import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import apiService from '../services/api';

interface Item {
  id: number;
  barcode?: string | null;
  crtnNo?: string | null;
  netWeight?: number | null;
  location?: string | null;
}
interface Entry {
  id: number;
  yarnName?: string;
  lot?: string;
  buyerName?: string;
  color?: string;
  items?: Item[];
}

// Shared styles. The base .yl-* rules apply on screen (preview) and print; the @media print block
// hides the whole app + modal and shows only the print-only label sheet, which sits in normal
// document flow so it paginates across A4 pages (break-inside:avoid keeps a label whole).
const PRINT_CSS = `
  .yl-print-only { display: none; }
  .yl-sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .yl-label { border: 1px dashed #94a3b8; border-radius: 6px; padding: 8px; display: flex; gap: 8px; align-items: center; break-inside: avoid; page-break-inside: avoid; }
  .yl-label svg { flex-shrink: 0; }
  .yl-info { font-size: 10px; line-height: 1.35; color: #0e131b; min-width: 0; }
  .yl-info .yl-code { font-weight: 700; font-size: 12px; letter-spacing: 0.02em; }
  .yl-info .yl-muted { color: #64748b; }
  @media print {
    @page { size: A4; margin: 10mm; }
    body > *:not(.yl-print-only) { display: none !important; }
    .yl-print-only { display: block !important; }
    /* index.css has a global print rule (body > * visibility:hidden !important) that
       inherits down and blanks these labels. Re-assert visibility on our print subtree. */
    .yl-print-only, .yl-print-only * { visibility: visible !important; }
  }
`;

// Renders an A4 sheet of QR labels (one per carton) for a Yarn Inward entry and prints it.
// Each label = QR of the carton's unique barcode + human-readable box details.
const YarnLabelPrint: React.FC<{ entryId: number | null; onClose: () => void }> = ({ entryId, onClose }) => {
  const [entry, setEntry] = useState<Entry | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entryId == null) { setEntry(null); return; }
    let cancelled = false;
    setLoading(true);
    apiService.get(`/yarn-masters/${entryId}`)
      .then((d: any) => { if (!cancelled) setEntry(d?.entry || d); })
      .catch(() => { if (!cancelled) setEntry(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entryId]);

  if (entryId == null) return null;

  const items = (entry?.items || []).filter((it) => it.barcode);

  const renderSheet = () => (
    <div className="yl-sheet">
      {items.map((it) => (
        <div key={it.id} className="yl-label">
          <QRCodeSVG value={String(it.barcode)} size={64} level="M" />
          <div className="yl-info">
            <div className="yl-code">{it.barcode}</div>
            <div>{entry?.yarnName || '—'}</div>
            <div className="yl-muted">{entry?.lot ? `Lot ${entry.lot}` : ''}{it.location ? ` · ${it.location}` : ''}</div>
            <div className="yl-muted">Box {it.crtnNo || '—'}{it.netWeight != null ? ` · ${it.netWeight} kg` : ''}</div>
            <div className="yl-muted">{entry?.buyerName || ''}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <style>{PRINT_CSS}</style>

      {/* On-screen modal (preview + controls). Hidden entirely when printing. */}
      {createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBF1]">
              <div>
                <h3 className="text-lg font-bold text-[#1B202A]">Print Box Labels</h3>
                <p className="text-xs text-[rgba(14,19,27,0.5)]">
                  {entry ? `${entry.yarnName || '—'}${entry.lot ? ` · Lot ${entry.lot}` : ''} · ${items.length} label${items.length === 1 ? '' : 's'}` : 'Loading…'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => window.print()} disabled={!items.length}
                  className="inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#0B2278] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                  Print
                </button>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F8F9FC] text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto bg-[#F8F9FC]">
              {loading && <p className="text-sm text-gray-500 text-center py-8">Loading labels…</p>}
              {!loading && !items.length && <p className="text-sm text-gray-500 text-center py-8">No printable boxes found for this entry.</p>}
              {!loading && !!items.length && <div className="bg-white p-3 rounded-lg">{renderSheet()}</div>}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Print-only sheet — normal flow so it paginates across pages. Hidden on screen. */}
      {!!items.length && createPortal(
        <div className="yl-print-only">{renderSheet()}</div>,
        document.body
      )}
    </>
  );
};

export default YarnLabelPrint;
